import { ReactNode } from 'react';
import { Payload } from '../Payload';
import { SortColumn } from '../../API/APIObjects';

/**
 * The generic type for the functions that get filters from the session storage.
 */
export type GetFiltersFn<O extends object> = () => FilterStateType<O>;
/**
 * A generic type for the functions that set filters in the session storage.
 */
export type SetFiltersFn<O extends object> = (props: O) => void;

/**
 * The types of dispatches for the filter context that are available.
 */
export enum FilterDispatchTypes {
    /**
     * Will always exist and only updates the properties on the filter object
     * that was passed in as the first type parameter for the factory function
     * that created the filter context.
     */
    UpdateFilters = 'UPDATE_FILTERS',
    /**
     * Is not available unless the second type parameter is set to true on the
     * factory function that created the filter context.
     */
    UpdateSorts = 'UPDATE_SORTS',
    /**
     * Is not available unless the third type parameter is set to true on the
     * factory function that created the filter context.
     */
    UpdateRowsPerPage = 'UPDATE_ROWS_PER_PAGE'
}

/**
 * A generic type for defining the payloads of various dispatches for the filter context.
 */
export type FilterPayload<O extends object, S extends boolean, R extends boolean> = {
    [FilterDispatchTypes.UpdateFilters]: { filters: O; };
    [FilterDispatchTypes.UpdateSorts]: { sorts: S extends true ? SupportFiltersColumnSort : never; };
    [FilterDispatchTypes.UpdateRowsPerPage]: { rowsPerPage: R extends true ? SupportRowsPerPage : never; };
};

/**
 * A generic type that maps the dispatches and what the payloads of those dispatches require.
 */
export type FilterActions<O extends object, S extends boolean, R extends boolean> = Payload<FilterPayload<O, S, R>>[keyof Payload<FilterPayload<O, S, R>>];

/**
 * A generic type for the state of the filter context.
 */
export type FilterStateType<O extends object> = O;

/**
 * The props required by the generic filter provider.
 */
export interface FilterProviderProps {
    /**
     * The child nodes that will render within the filter context scope.
     */
    children?: ReactNode;
}

/**
 * This type map is used to indicate to the `getSessionStorageValue` function what type we
 * expect it to return.
 * It also indicates to the function what type of type checking it will need to do so we
 * can verify that the values retrieved from the session storage are of the correct type.
 *
 * When `U` type parameter is true, we treat whatever the type that would be normally returned
 * as an optional type.
 */
export interface PossibleFilterTypes<U extends boolean> {
    'string_array': U extends true ? (string[] | undefined) : string[];
    'number_array': U extends true ? (number[] | undefined) : number[];
    'sortColumn_array': U extends true ? (SortColumn[] | undefined) : SortColumn[];
    'date': U extends true ? (Date | null | undefined) : Date | null;
    'string': U extends true ? (string | undefined) : string;
    'rowsPerPageNumber': U extends true ? (number | undefined) : number;
    'number': U extends true ? (number | undefined) : number;
}

/**
 * Object for the column sorts array so it can be intersected with whatever filter object
 * is given to the create filter context factory function.
 */
export interface SupportFiltersColumnSort {
    /**
     * An array of the column sorts the user had selected.
     */
    columnSorts: SortColumn[];
}
/**
 * Object for the rows per page number so that it can be intersected with whatever filter object
 * is given to the create filter context factory function.
 */
export interface SupportRowsPerPage {
    /**
     * A number of rows to grab per page.
     */
    retainedRowsPerPage: number;
}

/**
 * Allows for the user when using the factory function to create the filter context to
 * choose to either enable or disable retaining column sorting.
 */
export type IntersectWithSort<O extends object, S extends boolean> = S extends true ? O & SupportFiltersColumnSort : O;

/**
 * Allows for the user when using the factory function to create the filter context to
 * choose to either enable or disable retaining rows per page, and then check with the {@link IntersectWithSort} type.
 */
export type IntersectWithRowsPerPage<
    O extends object,
    S extends boolean,
    R extends boolean
> = R extends true ? SupportRowsPerPage & IntersectWithSort<O, S> : IntersectWithSort<O, S>;

/**
 * As const object to act as a type for the possible options that can be chosen from the
 * Results Per Page select options.
 */
export const RowsPerPageOptions = {
    '25': 25,
    '50': 50,
    '75': 75,
    '100': 100,
} as const;